import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { getActiveFeatureFlags } from '../../services/featureFlags';
import { ActiveFeatureFlag } from '../../interfaces/featureFlag.interface';
import { logoutUser } from './userSlice';
import { startMasquerade, stopMasquerade } from './masqueradeSlice';

interface FeatureFlagState {
  flags: ActiveFeatureFlag[] | null;
  isFetching: boolean;
}

const initialState: FeatureFlagState = {
  flags: null,
  isFetching: false,
};

/**
 * Fetch the active (in-window) flags + their type targeting. Skips starting a
 * second request while one is already in flight, so callers can dispatch this
 * freely (e.g. on every consumer mount) without piling up duplicate requests.
 */
export const fetchActiveFeatureFlags = createAsyncThunk(
  'featureFlags/fetchActive',
  async (_: void, { rejectWithValue }) => {
    try {
      return await getActiveFeatureFlags();
    } catch (error: any) {
      console.error('Failed to load active feature flags', error);
      return rejectWithValue(error?.message ?? 'Failed to load active feature flags');
    }
  },
  {
    condition: (_, { getState }) => !(getState() as { featureFlags: FeatureFlagState }).featureFlags.isFetching,
  },
);

const featureFlagSlice = createSlice({
  name: 'featureFlags',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchActiveFeatureFlags.pending, (state) => {
      state.isFetching = true;
    });
    builder.addCase(fetchActiveFeatureFlags.fulfilled, (state, action) => {
      state.flags = action.payload;
      state.isFetching = false;
    });
    builder.addCase(fetchActiveFeatureFlags.rejected, (state) => {
      state.isFetching = false;
    });
    // The active flags (and their targeting) are user-specific — clear them so a
    // login, logout, or masquerade change never leaks the previous identity's flags.
    builder.addCase(logoutUser, () => initialState);
    builder.addCase(startMasquerade, () => initialState);
    builder.addCase(stopMasquerade, () => initialState);
  },
});

export default featureFlagSlice.reducer;
